/**
 * Operations expenses (UPGRADE-01 R5). Record an expense — debit an expense
 * account, credit a user-selected source (cash/bank/mobile-money) account — and
 * see recent expenses. Read-gated on `ledger.read`; the entry form needs
 * `expense.record`.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "../components/Form.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface AccountOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly category: string;
  readonly isActive: boolean;
}

interface ExpenseView {
  readonly id: string;
  readonly expenseAccountName: string;
  readonly sourceAccountName: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly vendor: string | null;
  readonly description: string | null;
  readonly occurredAt: string;
}

const SOURCE_CATEGORIES = new Set(["cash_on_hand", "bank", "mobile_money"]);

export function ExpensesPage(): JSX.Element {
  const auth = useAuth();
  const perms = auth.user?.permissions ?? [];
  const canRead = perms.includes("ledger.read");
  const canRecord = perms.includes("expense.record");
  const qc = useQueryClient();

  const accountsQ = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => api.get<readonly AccountOption[]>("/api/accounting/accounts"),
    enabled: canRead,
  });
  const expensesQ = useQuery({
    queryKey: ["accounting", "expenses"],
    queryFn: () => api.get<readonly ExpenseView[]>("/api/expenses"),
    enabled: canRead,
  });

  const expenseAccounts = useMemo(
    () => (accountsQ.data ?? []).filter((a) => a.isActive && a.type === "expense"),
    [accountsQ.data],
  );
  const sourceAccounts = useMemo(
    () => (accountsQ.data ?? []).filter((a) => a.isActive && SOURCE_CATEGORIES.has(a.category)),
    [accountsQ.data],
  );

  const [expenseAccountId, setExpenseAccountId] = useState("");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: (body: {
      expenseAccountId: string;
      sourceAccountId: string;
      amountMinor: number;
      vendor?: string;
      description?: string;
    }) => api.post("/api/expenses", body),
    onSuccess: () => {
      setExpenseAccountId("");
      setSourceAccountId("");
      setAmount("");
      setVendor("");
      setDescription("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["accounting", "expenses"] });
      void qc.invalidateQueries({ queryKey: ["accounting", "accounts"] });
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  if (!canRead) {
    return (
      <div className="p-6 text-slate-600">
        You do not have access to expenses (requires <code>ledger.read</code>).
      </div>
    );
  }

  const expenses = expensesQ.data ?? [];

  function submit(): void {
    setError(null);
    const n = Number(amount);
    if (!expenseAccountId) return setError("Pick an expense account.");
    if (!sourceAccountId) return setError("Pick the account it's paid from.");
    if (!Number.isInteger(n) || n <= 0) return setError("Amount must be a positive whole number.");
    record.mutate({
      expenseAccountId,
      sourceAccountId,
      amountMinor: n,
      ...(vendor.trim() ? { vendor: vendor.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Operations expenses</h1>
        <p className="text-sm text-slate-500">
          Each expense posts a balanced journal — debit the expense account, credit the source.
        </p>
      </div>

      {canRecord ? (
        <Card>
          <CardHeader>
            <CardTitle>Record expense</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Expense account">
                <Select
                  value={expenseAccountId}
                  onChange={(e) => setExpenseAccountId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {expenseAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Paid from" hint="cash / bank / mobile money">
                <Select
                  value={sourceAccountId}
                  onChange={(e) => setSourceAccountId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {sourceAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Amount" hint="minor units">
                <TextInput
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Vendor" hint="optional">
                <TextInput value={vendor} onChange={(e) => setVendor(e.target.value)} />
              </Field>
            </div>
            <Field label="Description" hint="optional">
              <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <FormError message={error} />
            <Button onClick={submit} disabled={record.isPending}>
              {record.isPending ? "Recording…" : "Record expense"}
            </Button>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent expenses</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <Tr>
                <Th>Date</Th>
                <Th>Expense</Th>
                <Th>Paid from</Th>
                <Th>Vendor</Th>
                <Th className="text-right">Amount</Th>
              </Tr>
            </THead>
            <TBody>
              {expenses.length === 0 ? (
                <Tr>
                  <Td colSpan={5} className="text-slate-400">
                    No expenses recorded yet.
                  </Td>
                </Tr>
              ) : (
                expenses.map((e) => (
                  <Tr key={e.id}>
                    <Td className="text-slate-500">{e.occurredAt.slice(0, 10)}</Td>
                    <Td>{e.expenseAccountName}</Td>
                    <Td className="text-slate-500">{e.sourceAccountName}</Td>
                    <Td className="text-slate-500">{e.vendor ?? ""}</Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(e.amountMinor, e.currency)}
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}
